import { formatNumber, t, tDynamic } from '@glory/i18n';
import {
  groupTitleKey,
  permissionHintKey,
  permissionsByGroup,
  permissionTitleKey,
  type Role,
  ROLE_PERMISSIONS,
  ROLES,
} from '@glory/identity/domain';
import {
  Callout,
  InfoHint,
  notify,
  notifyError,
  PageHeader,
  SettingsSection,
  SkeletonRows,
  SwitchField,
} from '@glory/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Check, Minus } from 'lucide-react';

import { roleDescription, roleTitle } from '../../../features/team/roles.tsx';
import { orpc } from '../../../lib/orpc.ts';
import { PersonAvatar } from '../../../lib/people.tsx';
import { requireSettingsPermission, usePermissions } from '../../../lib/permissions.ts';

export const Route = createFileRoute('/_app/settings/roles')({
  beforeLoad: requireSettingsPermission('roles.assign'),
  component: RolesPage,
});

/**
 * «Роли и доступ» (только чтение): матрица «группа → право → роли» из каталога разрешений и кто сейчас в какой роли.
 * Меняются роли в карточке сотрудника — здесь только объяснение.
 */
function RolesPage() {
  const { can } = usePermissions();
  return (
    <div className="flex flex-col gap-4 px-4 py-5 md:px-6">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2 whitespace-normal">
            {t('access.ui.page.title')}
            <InfoHint
              hint={{
                title: t('access.ui.page.hint.title'),
                text: t('access.ui.page.hint.text'),
                example: t('access.ui.page.hint.example'),
              }}
            />
          </span>
        }
      />
      <p className="max-w-[70ch] text-small text-muted">{t('access.ui.page.lead')}</p>
      {can('studio.manage') ? <SecurityPolicy /> : null}
      {can('studio.manage') ? <EncryptionStatus /> : null}
      <PeopleByRole />
      <Matrix />
    </div>
  );
}

/** «Защита входа»: обязательная 2FA для тех, кто назначает роли. Переключатель меняется сразу, сбой — откат. */
function SecurityPolicy() {
  const queryClient = useQueryClient();
  const policy = useQuery(orpc.identity.security.get.queryOptions());
  const key = orpc.identity.security.get.queryKey();
  const update = useMutation(
    orpc.identity.security.update.mutationOptions({
      onMutate: async ({ requireAdminTwoFactor }) => {
        await queryClient.cancelQueries({ queryKey: key });
        const before = policy.data;
        queryClient.setQueryData(key, { requireAdminTwoFactor });
        return { before };
      },
      onError: (error, _input, context) => {
        if (context?.before) queryClient.setQueryData(key, context.before);
        notifyError(error.message);
      },
      onSuccess: () => {
        notify(t('security.policy.saved'));
      },
    }),
  );
  return (
    <SettingsSection title={t('security.policy.title')} lead={t('security.policy.lead')}>
      <div className="border-t border-border-soft px-4 py-3">
        {policy.isError ? (
          <Callout>{policy.error.message}</Callout>
        ) : (
          <SwitchField
            label={t('security.policy.adminTwoFactor')}
            description={t('security.policy.adminTwoFactorHint')}
            checked={policy.data?.requireAdminTwoFactor ?? false}
            disabled={policy.isPending}
            onCheckedChange={(on) => {
              update.mutate({ requireAdminTwoFactor: on });
            }}
          />
        )}
      </div>
    </SettingsSection>
  );
}

/** Подпись версии ключа: открытое после миграции, старый ключ из AUTH_SECRET или версия из секрета. */
const versionLabel = (version: string): string =>
  version === 'plain'
    ? t('security.encryption.plain')
    : version === 'legacy'
      ? t('security.encryption.legacy')
      : t('security.encryption.version', { version });

/** «Шифрование данных»: активная версия мастер-ключа и сколько записей ещё ждут перешифрования. */
function EncryptionStatus() {
  const status = useQuery(orpc.identity.security.encryption.queryOptions());
  const stale =
    status.data?.versions.filter((v) => v.version !== status.data.activeVersion && v.count > 0) ?? [];
  const pending = stale.reduce((sum, v) => sum + v.count, 0);
  return (
    <SettingsSection
      title={
        <span className="inline-flex items-center gap-2">
          {t('security.encryption.title')}
          <InfoHint
            hint={{
              title: t('security.encryption.hint.title'),
              text: t('security.encryption.hint.text'),
              example: t('security.encryption.hint.example'),
            }}
          />
        </span>
      }
      lead={t('security.encryption.lead')}
    >
      <div className="flex flex-col gap-1 border-t border-border-soft px-4 py-3 text-small">
        {status.isError ? (
          <Callout>{status.error.message}</Callout>
        ) : status.data === undefined ? (
          <SkeletonRows rows={2} />
        ) : (
          <>
            <span>{t('security.encryption.active', { version: status.data.activeVersion })}</span>
            <span className="text-muted">
              {pending === 0
                ? t('security.encryption.done')
                : `${t('security.encryption.pending', { count: formatNumber(pending) })} · ${stale
                    .map((v) => `${versionLabel(v.version)}: ${formatNumber(v.count)}`)
                    .join(', ')}`}
            </span>
          </>
        )}
      </div>
    </SettingsSection>
  );
}

function PeopleByRole() {
  const team = useQuery(orpc.org.team.list.queryOptions());
  if (team.isError) return <Callout>{team.error.message}</Callout>;
  const people = (team.data?.items ?? []).filter((person) => person.active);
  return (
    <SettingsSection title={t('access.ui.page.people')}>
      {team.isPending ? (
        <div className="px-4 pb-4">
          <SkeletonRows rows={3} />
        </div>
      ) : (
        <ul className="grid gap-px border-t border-border-soft md:grid-cols-3">
          {ROLES.map((role) => (
            <RolePeople
              key={role}
              role={role}
              // eslint-disable-next-line glory/no-role-checks -- справочная страница: кто носит роль, а не доступ
              people={people.filter((person) => person.roles.includes(role))}
            />
          ))}
        </ul>
      )}
    </SettingsSection>
  );
}

function RolePeople({
  role,
  people,
}: {
  role: Role;
  people: readonly { readonly id: string; readonly name: string }[];
}) {
  return (
    <li className="flex min-w-0 flex-col gap-2 px-4 py-3">
      <div className="flex flex-col gap-0.5">
        <span className="font-bold">{roleTitle(role)}</span>
        <span className="text-caption text-muted">{roleDescription(role)}</span>
      </div>
      {people.length === 0 ? (
        <span className="text-small text-muted">{t('access.ui.page.nobody')}</span>
      ) : (
        <ul className="flex flex-col gap-1">
          {people.map((person) => (
            <li key={person.id} className="flex min-w-0 items-center gap-1.5 text-small">
              <PersonAvatar person={person} />
              <span className="truncate font-semibold">{person.name}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function Cell({ granted }: { granted: boolean }) {
  const Icon = granted ? Check : Minus;
  return (
    <td className="w-24 px-2 py-1.5 text-center">
      <Icon
        role="img"
        aria-label={granted ? t('access.ui.page.granted') : t('access.ui.page.denied')}
        className={granted ? 'mx-auto size-4' : 'mx-auto size-4 text-muted'}
      />
    </td>
  );
}

function Matrix() {
  return (
    <SettingsSection title={t('access.ui.page.matrix')}>
      <div className="overflow-x-auto border-t border-border-soft">
        <table className="w-full min-w-[34rem] border-collapse text-small">
          <thead>
            <tr className="text-left text-caption text-muted">
              <th scope="col" className="px-4 py-2 font-bold">
                {t('access.ui.page.permission')}
              </th>
              {ROLES.map((role) => (
                <th key={role} scope="col" className="w-24 px-2 py-2 text-center font-bold">
                  {roleTitle(role)}
                </th>
              ))}
            </tr>
          </thead>
          {permissionsByGroup().map(({ group, permissions }) => (
            <tbody key={group} className="border-t border-border-soft">
              <tr>
                <th
                  scope="colgroup"
                  colSpan={ROLES.length + 1}
                  className="bg-surface-2 px-4 py-1.5 text-left text-caption font-bold text-muted uppercase"
                >
                  {tDynamic(groupTitleKey(group))}
                </th>
              </tr>
              {permissions.map((permission) => (
                <tr key={permission} className="border-t border-border-soft">
                  <th scope="row" className="px-4 py-1.5 text-left font-semibold">
                    <span className="inline-flex items-center gap-1.5">
                      {tDynamic(permissionTitleKey(permission))}
                      <InfoHint
                        hint={{
                          title: tDynamic(permissionTitleKey(permission)),
                          text: tDynamic(permissionHintKey(permission)),
                        }}
                      />
                    </span>
                  </th>
                  {ROLES.map((role) => (
                    <Cell key={role} granted={ROLE_PERMISSIONS[role].has(permission)} />
                  ))}
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      </div>
    </SettingsSection>
  );
}
